import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AreaService } from 'shared-lib';
import { ToastrService } from 'ngx-toastr';
import { AreaCrud, getInitAreaCrud, mapObjToCreateObj } from '../../../models/area.model';
import { RegionService } from 'shared-lib';
import { Region } from 'shared-lib';
import { UtilitySharedService } from '../../../shared/_services/utility-shared.service';

@Component({
  selector: 'app-crud-district',
  templateUrl: './crud-district.component.html',
  styleUrl: './crud-district.component.scss',
  standalone: false
})
export class CrudDistrictComponent implements OnInit {
  pageSize: number = 10;
  currentPage: number = 1;
  utilityId: number = 1;
  count: number = 0;
  updatedName: string = '';
  updatedCode: string = '';
  updatedNumber: string = '';
  updatedRegionId: number | null = null;
  areas: AreaCrud[] = [];
  selectedArea: AreaCrud = getInitAreaCrud();
  regions: Region[] = [];
  searchValue: string = "";
  isEditMode: boolean = false;

  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Input() reloadTableInput: boolean = false;
  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }

  @ViewChild('closeEditModal') closeEditModal: ElementRef;

  constructor(
    private areaService: AreaService,
    private toastr: ToastrService,
    private regionService: RegionService,
    private utilitySharedService: UtilitySharedService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);
    this.reloadTable(1);
    this.getAllRegions();
  }

  openAddModal() {
    this.isEditMode = false;
    this.selectedArea = getInitAreaCrud();
    this.updatedName = '';
    this.updatedCode = '';
    this.updatedNumber = '';
    this.updatedRegionId = null;
  }

  openEditModal() {
    this.isEditMode = true;
    this.fillInputFields();
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.reloadTable(newPage);
  }

  private getAllAreas(dataIn: any) {
    this.areaService.getAll(dataIn).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.areas = responseData.data.data;
          this.count = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  getAllRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data.map((region: Region) => ({
            ...region,
            isExpanded: false,
          }));
        } else if (response?.status === 204) {
          this.regions = [];
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred while loading districts');
      },
    });
  }

  getFilterObject() {
    return {
      searchValue: this.searchValue
    };
  }

  save() {
    const isNumberValid = this.updatedNumber ? /^7\d{3}$/.test(this.updatedNumber) : true;
    if (!this.updatedName) {
      this.toastr.warning("Name can't be empty!");
      return;
    }
    if (!this.updatedCode) {
      this.toastr.warning("Code can't be empty!");
      return;
    }
    if (!isNumberValid) {
      this.toastr.warning("Number must be 4 digits and to start with 7!");
      return;
    }
    if (!this.updatedRegionId) {
      this.toastr.warning("Please select a region!");
      return;
    }

    this.selectedArea.name = this.updatedName;
    this.selectedArea.code = this.updatedCode;
    this.selectedArea.number = this.updatedNumber;
    this.selectedArea.region.id = Number(this.updatedRegionId);
    this.selectedArea.utilityId = this.utilityId;
    this.selectedArea.id = this.isEditMode ? this.selectedArea.id : 0;

    this.areaService.save(mapObjToCreateObj(this.selectedArea)).subscribe({
      next: (response) => {
        if (response.body.status === 200 || response.body.status === 'OK') {
          this.toastr.success("District successfully saved.");
          setTimeout(() => {
            this.reloadTable(1);
            this.closeEditModal?.nativeElement?.click();
          }, 500);
        } else {
          this.toastr.error(response.body.message || 'Failed to save district');
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred while saving district');
      },
    });
  }

  reloadTable(page: number | null = null) {
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId
    };
    this.getAllAreas(obj);
  }

  selectArea(area: AreaCrud) {
    this.selectedArea = area;
    this.openEditModal();
  }

  onDeleteArea(area: AreaCrud){
    this.selectedArea = area;
    this.isEditMode = false;
  }

  private fillInputFields() {
    this.updatedName = this.selectedArea.name;
    this.updatedCode = this.selectedArea.code;
    this.updatedNumber = this.selectedArea.number;
    this.updatedRegionId = this.selectedArea.region?.id || null;
  }

  onSearch() {
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId
    };
    this.getAllAreas(dataIn);
  }

  onSearchChange() {
    if (this.searchValue === '' || this.searchValue.trim() === '') {
      this.onSearch();
    }
  }

  deleteDistrict() {
    this.areaService.delete(this.selectedArea.id).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.onPageChange(1);
          this.toastr.success(responseData?.message);
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }
}